import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { CompositionService } from "../../../services/CompositionService";
import { FinetuneRunService } from "../../../services/FinetuneRunService";
import { FinetuneRunNew } from "../../../model_gen/finetuning";
import CompositionOverview from "./CompositionOverview";
import VersionsTable from "../../shared/VersionsTable";
import FinetuneRunsTable from "../FinetuneRun/FinetuneRunsTable";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import OverflowTabs, { TabItem } from "../../ui/overflow-tabs";
import { 
  EditIcon,
  PlusIcon,
  AppWindowIcon,
  PieChartIcon,
  GitBranchIcon,
  RocketIcon
} from "lucide-react";
import CompositionMembersTable from "./CompositionMembersTable";

interface CompositionMember {
  dataset_id: number;
  dataset_name: string;
  dataset_description: string;
  sampling_type: string;
  sampling_weight: number;
  origin_dataset_id: number;
  origin_dataset_name: string;
  origin_dataset_description: string;
  request_count: number;
  major_version?: number;
  minor_version?: number;
}

interface CompositionData {
  id: number;
  name: string;
  description: string;
  major_version: number;
  minor_version: number;
  parent_composition_id?: number;
  meta: any;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  date_created: number;
  base_type: string;
  members: CompositionMember[];
  member_count: number;
}

interface CompositionVersion {
  id: number;
  name: string;
  description: string;
  major_version: number;
  minor_version: number;
  parent_composition_id?: number;
  meta: any;
  application_id?: number;
  intent_id?: number;
  intent_group_id?: number;
  date_created: number;
  base_type: string;
  member_count: number;
  total_request_count: number;
  depth: number;
  path: string;
  is_current: boolean;
}

interface SortConfig {
  key: keyof FinetuneRunNew | null;
  direction: "asc" | "desc";
}

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const CompositionDetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [composition, setComposition] = useState<CompositionData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [versions, setVersions] = useState<CompositionVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(false);
  const [finetuneRuns, setFinetuneRuns] = useState<FinetuneRunNew[]>([]);
  const [finetuneRunsLoading, setFinetuneRunsLoading] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "desc" });

  // Update initial tab from URL
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Fetch composition details
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      CompositionService.getComposition(id, setIsLoading, apiKey).subscribe(
        (data: CompositionData) => {
          if (data) {
            setComposition(data);
          } else {
            console.error("Received empty composition data");
          }
        },
        (error: any) => {
          console.error("Error fetching composition:", error);
        }
      );
    }
  }, [id, apiKey]);

  // Fetch versions when the versions tab is activated
  useEffect(() => {
    if (id && activeTab === "versions" && versions.length === 0) {
      setVersionsLoading(true);
      CompositionService.getCompositionVersions(id, setVersionsLoading, apiKey).subscribe(
        (data: CompositionVersion[]) => {
          if (data) {
            setVersions(data);
          } else {
            console.error("Received empty versions data");
          }
        },
        (error: any) => {
          console.error("Error fetching versions:", error);
        }
      );
    }
  }, [id, apiKey, activeTab, versions.length]);

  // Fetch finetune runs when the finetune runs tab is activated
  useEffect(() => {
    if (id && activeTab === "finetune-runs" && finetuneRuns.length === 0) {
      setFinetuneRunsLoading(true);
      FinetuneRunService.getFinetuneRunsByCompositionId(Number(id), 0, 100, setFinetuneRunsLoading, apiKey).subscribe(
        (response: { items: FinetuneRunNew[]; total: number }) => {
          if (response && response.items) {
            setFinetuneRuns(response.items);
          } else {
            console.error("Received empty finetune runs data");
          }
        },
        (error: any) => {
          console.error("Error fetching finetune runs:", error);
        }
      );
    }
  }, [id, apiKey, activeTab, finetuneRuns.length]);

  const handleSort = (key: keyof FinetuneRunNew) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedFinetuneRuns = React.useMemo(() => {
    const sortableRuns = [...finetuneRuns];
    if (sortConfig.key) {
      sortableRuns.sort((a, b) => {
        const aValue = a[sortConfig.key!];
        const bValue = b[sortConfig.key!];
        
        if (aValue < bValue) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Default sort by date_created descending
      sortableRuns.sort((a, b) => {
        const dateA = a.date_created > 0 ? a.date_created : 0;
        const dateB = b.date_created > 0 ? b.date_created : 0;
        return dateB - dateA;
      });
    }
    return sortableRuns;
  }, [finetuneRuns, sortConfig]);

  const handleRunClick = (run: FinetuneRunNew) => {
    navigate(`/build/finetuning/${run.id}`);
  };

  const refreshFinetuneRuns = () => {
    if (id) {
      setFinetuneRunsLoading(true);
      FinetuneRunService.getFinetuneRunsByCompositionId(Number(id), 0, 100, setFinetuneRunsLoading, apiKey).subscribe(
        (response: { items: FinetuneRunNew[]; total: number }) => {
          if (response && response.items) {
            setFinetuneRuns(response.items);
          }
        },
        (error: any) => {
          console.error("Error fetching finetune runs:", error);
        }
      );
    }
  };

  // Tab configuration
  const tabDefinitions: TabDefinition[] = [
    {
      key: "overview",
      label: "Overview",
      icon: <AppWindowIcon className="w-4 h-4" />,
      content: composition ? <CompositionOverview composition={composition} /> : null
    },
    {
      key: "members",
      label: "Members",
      icon: <PieChartIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Member Datasets</h3>
            <p className="text-sm text-muted-foreground">View datasets that comprise this composition</p>
          </div>
          <CompositionMembersTable
            members={composition?.members || []}
            loading={isLoading}
            totalRequestCount={composition?.members.reduce((sum, member) => sum + member.request_count, 0) || 0}
          />
        </div>
      )
    },
    {
      key: "versions",
      label: "Versions",
      icon: <GitBranchIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-gc-med text-foreground">Version History</h3>
            <p className="text-sm text-muted-foreground">All versions of this composition, ordered by creation date</p>
          </div>
          <VersionsTable
            versions={versions.map(v => ({
              id: v.id,
              major_version: v.major_version,
              minor_version: v.minor_version,
              name: v.name,
              request_count: v.total_request_count || 0,
              date_created: v.date_created
            }))}
            loading={versionsLoading}
            currentVersionId={id || ""}
          />
        </div>
      )
    },
    {
      key: "finetune-runs",
      label: "Finetune Runs",
      icon: <RocketIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Finetune Runs</h3>
              <p className="text-sm text-muted-foreground">All finetune runs for this composition</p>
            </div>
            <Button
              onClick={() => navigate(`/build/finetuning/new?composition_id=${id}`)}
              className="gap-2"
            >
              <PlusIcon className="w-4 h-4" />
              New Finetune Run
            </Button>
          </div>
          <FinetuneRunsTable
            finetuneRuns={sortedFinetuneRuns}
            loading={finetuneRunsLoading}
            datasets={{}} // Empty datasets since this is composition-based
            sortConfig={sortConfig}
            onSort={handleSort}
            onRunClick={handleRunClick}
            onRefresh={refreshFinetuneRuns}
            apiKey={apiKey}
            setIsLoading={setFinetuneRunsLoading}
            currentPage={1}
            totalPages={1}
            totalItems={finetuneRuns.length}
            itemsPerPage={100}
            onPageChange={() => {}} // No pagination needed since we fetch all runs
          />
        </div>
      )
    }
  ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map(tab => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon
  }));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/build/compositions")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Compositions
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">{composition?.name}</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                    {composition?.name}
                  </h1>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                    v{composition?.major_version}.{composition?.minor_version}
                  </span>
                </div>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  View composition details and member datasets
                </p>
              </div>
              <Button
                onClick={() => navigate(`/build/compositions/${id}/edit`)}
                className="gap-2"
              >
                <EditIcon className="w-4 h-4" />
                Edit Composition
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <OverflowTabs 
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className="border-b border-border px-8 py-4"
            />

            {/* Tab Content */}
            <div className="p-8">
              {tabDefinitions.find(tab => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompositionDetailView; 